import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet, Loader2, Download, Upload, Search, ChevronUp, ChevronDown } from "lucide-react";
import EmptyState from "@/shared/components/ui/empty-state/empty-state.tsx";
import { Button } from "@/ui/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/ui/tabs";
import { Input } from "@/ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/components/ui/select";
import { useReportJob, useDownloadReportArtifact } from "@/export/api/queries/reports.queries";
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableHead, 
  TableRow, 
  TableCell 
} from "@/ui/components/ui/table";
import { Trans, useTranslation } from "react-i18next";
import { ReportJob } from "@/export/types/reports";
import { formatRelativeTimeUTC } from "@/shared/utils/date/format-relative-time";
import { activeLocale, formatDateTime } from "@/shared/lib/i18n/formatters.ts";
import * as ExcelJS from 'exceljs';
import JSZip from 'jszip';

interface CellData {
  value: any;
  hyperlink?: string;
  imageData?: {
    buffer: ArrayBuffer;
    contentType: string;
    extension: string;
  };
}

interface ExportData {
  sheetName: string;
  headers: string[];
  rows: CellData[][];
}

interface DataTableProps {
  headers: string[];
  data: CellData[][];
  searchTerm: string;
  sortColumn: number | null;
  sortDirection: 'asc' | 'desc';
  currentPage: number;
  itemsPerPage: number;
  onSort: (columnIndex: number) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (term: string) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  formatCellValue: (cellData: CellData, header?: string, columnIndex?: number) => string | React.ReactNode;
}

// Image cell component for displaying images in table cells
const ImageCell: React.FC<{ 
  src?: string; 
  imageData?: { buffer: ArrayBuffer; contentType: string; extension: string };
}> = ({ src, imageData }) => {
  const { t } = useTranslation("export");
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>('');

  // Create blob URL from image data or construct URL from src
  useEffect(() => {
    if (imageData) {
      // Create blob from ArrayBuffer
      const blob = new Blob([imageData.buffer], { type: imageData.contentType });
      const url = URL.createObjectURL(blob);
      setImageUrl(url);
      
      // Cleanup function to revoke the URL when component unmounts
      return () => URL.revokeObjectURL(url);
    } else if (src) {
      // Construct full image URL - adjust this based on your backend
      const getImageUrl = (imagePath: string): string => {
        // If it's already a full URL, return as is
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
          return imagePath;
        }
        
        // If it starts with pub/, it might be a relative path from your backend
        if (imagePath.startsWith('pub/')) {
          // Adjust this URL based on your backend configuration
          return `http://34.9.192.249:8080/${imagePath}`;
        }
        
        // For other paths, try to construct a reasonable URL
        return imagePath;
      };
      
      setImageUrl(getImageUrl(src));
    }
  }, [src, imageData]);

  if (!imageUrl) {
    return (
      <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
        <span className="text-center">
          <Trans t={t} i18nKey="imageCell.none" components={{ br: <br /> }} />
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      {imageLoading && !imageError && (
        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
      {!imageError ? (
        <img
          src={imageUrl}
          alt={src || t("imageCell.alt")}
          className="max-w-16 max-h-16 object-contain rounded cursor-pointer hover:scale-105 transition-transform"
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageError(true);
            setImageLoading(false);
          }}
          onClick={() => window.open(imageUrl, '_blank')}
          title={t("imageCell.viewFullSize", { name: src || t("imageCell.alt") })}
        />
      ) : (
        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
          <span className="text-center">
            <Trans t={t} i18nKey="imageCell.error" components={{ br: <br /> }} />
          </span>
        </div>
      )}
    </div>
  );
};

const DataTable: React.FC<DataTableProps> = ({ 
  headers, 
  data, 
  searchTerm, 
  sortColumn, 
  sortDirection, 
  currentPage, 
  itemsPerPage, 
  onSort, 
  onPageChange,
  onSearchChange,
  onItemsPerPageChange,
  formatCellValue 
}) => {
  const { t } = useTranslation("export");

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data;
    
    // Apply search filter
    if (searchTerm) {
      filtered = data.filter(row => 
        row.some(cell => 
          String(cell.value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    
    // Apply sorting
    if (sortColumn !== null) {
      filtered = [...filtered].sort((a, b) => {
        const aCell = a[sortColumn];
        const bCell = b[sortColumn];
        const aVal = aCell?.value;
        const bVal = bCell?.value;
        
        // Handle null/undefined values
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        
        // Handle numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        // Handle strings
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (sortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }
    
    return filtered;
  }, [data, searchTerm, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedData.slice(startIndex, endIndex);
  }, [filteredAndSortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t("table.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            onPageChange(1); // Reset to first page when searching
          }}
          className="max-w-sm"
        />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {headers.map((header, index) => (
                <TableHead 
                  key={index} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onSort(index)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{header}</span>
                    {sortColumn === index && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex}>
                    {formatCellValue(cell, headers[cellIndex], cellIndex)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-muted-foreground">
            {t("table.showing", {
              from: ((currentPage - 1) * itemsPerPage) + 1,
              to: Math.min(currentPage * itemsPerPage, filteredAndSortedData.length),
              total: filteredAndSortedData.length,
            })}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">{t("table.show")}</span>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => {
              onItemsPerPageChange(parseInt(value));
              onPageChange(1); // Reset to first page when changing page size
            }}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">{t("table.perPage")}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t("table.previous")}
          </Button>
          <span className="text-sm">
            {t("table.page", { current: currentPage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t("table.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ExportViewer: React.FC = () => {
  const { t } = useTranslation("export");
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [exportData, setExportData] = useState<ExportData[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Table state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const { data: jobResponse, isLoading: jobLoading, error: jobError } = useReportJob(jobId || "", !!jobId);
  const downloadMutation = useDownloadReportArtifact();

  const job = jobResponse?.data;
  const isLoading = jobLoading || isLoadingData;
  const error = jobError || dataError;

  const loadExcelData = async (fileBlob?: Blob) => {
    try {
      setIsLoadingData(true);
      setDataError(null);

      let blobToProcess = fileBlob;

      // If no file provided, download from server
      if (!blobToProcess && job?.id) {
        const downloadResponse = await downloadMutation.mutateAsync({ jobId: job.id });
        if (!downloadResponse?.data?.url) {
          throw new Error('Failed to get download URL');
        }

        const fileResponse = await fetch(downloadResponse.data.url);
        if (!fileResponse.ok) {
          throw new Error(`Failed to download file: ${fileResponse.statusText}`);
        }

        blobToProcess = await fileResponse.blob();
      }

      if (!blobToProcess) {
        throw new Error('No file to process');
      }
      
      // Handle different file types
      if (job?.type === 'boulders_report' && job?.artifact?.content_type === 'application/zip') {
        // For boulders report, it's a zip file containing an Excel file
        await parseZipFile(blobToProcess);
      } else if (job?.artifact?.content_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 blobToProcess.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        // For activity report, it's a direct Excel file
        await parseExcelFile(blobToProcess);
      } else {
        throw new Error('Unsupported file format');
      }

      setIsLoadingData(false);
    } catch (err) {
      console.error('Error loading Excel data:', err);
      setDataError(err instanceof Error ? err.message : t("viewer.errorDescription"));
      setIsLoadingData(false);
    }
  };

  const parseZipFile = async (zipBlob: Blob) => {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipBlob);
    
    // Find the Excel file in the zip
    const excelFile = Object.keys(zipContent.files).find(fileName => 
      fileName.endsWith('.xlsx') || fileName.endsWith('.xls')
    );
    
    if (!excelFile) {
      throw new Error('No Excel file found in the ZIP archive');
    }
    
    const excelBlob = await zipContent.files[excelFile].async('blob');
    await parseExcelFile(excelBlob);
  };

  const parseExcelFile = async (excelBlob: Blob) => {
    const arrayBuffer = await excelBlob.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    const sheets: ExportData[] = [];
    
    // Extract images from workbook media
    const mediaImages = new Map<number, { buffer: ArrayBuffer; contentType: string; extension: string }>();
    
    // Access media from workbook (ExcelJS internal structure)
    const media = (workbook as any).media;
    if (media && Array.isArray(media)) {
      for (const mediaItem of media) {
        if (mediaItem.buffer) {
          const contentType = mediaItem.type || mediaItem.contentType || "image/png";
          const extension = mediaItem.extension || 
            (contentType.includes("png") ? "png" : 
             contentType.includes("jpeg") ? "jpg" : 
             contentType.includes("gif") ? "gif" : "bin");
          
          mediaImages.set(mediaItem.index, {
            buffer: mediaItem.buffer,
            contentType,
            extension
          });
        }
      }
    }
    
    workbook.eachSheet((worksheet) => {
      const sheetName = worksheet.name;
      const rows: CellData[][] = [];
      
      // Get images from this worksheet
      const worksheetImages = worksheet.getImages();
      const imageMap = new Map<string, { buffer: ArrayBuffer; contentType: string; extension: string }>();
      
      for (const imageRef of worksheetImages) {
        const mediaImage = mediaImages.get(Number(imageRef.imageId));
        if (mediaImage) {
          // Create a key based on the image position/range
          const key = `${imageRef.range}`;
          imageMap.set(key, mediaImage);
        }
      }
      
      // Get all rows from the worksheet
      worksheet.eachRow((row) => {
        const rowData: CellData[] = [];
        
        // Get all cells from the row
        row.eachCell((cell) => {
          let cellValue: any = cell.value;
          let hyperlink: string | undefined;
          let imageData: { buffer: ArrayBuffer; contentType: string; extension: string } | undefined;
          
          // Extract hyperlink if present
          if (cell.hyperlink) {
            hyperlink = cell.hyperlink;
          }
          
          // Check if this cell contains an image
          const cellAddress = cell.address;
          for (const [range, imgData] of imageMap.entries()) {
            if (range.includes(cellAddress)) {
              imageData = imgData;
              break;
            }
          }
          
          // Handle different cell types
          if (cellValue === null || cellValue === undefined) {
            cellValue = '';
          } else if (typeof cellValue === 'object') {
            // Handle ExcelJS cell value objects
            if (cellValue.richText) {
              // Rich text - extract plain text
              cellValue = cellValue.richText.map((text: any) => text.text).join('');
            } else if (cellValue.text) {
              // Text object
              cellValue = cellValue.text;
            } else if (cellValue.formula) {
              // Formula - get the calculated result
              cellValue = cellValue.result || cellValue.formula;
            } else if (cellValue.date) {
              // Date object
              cellValue = cellValue.date;
            } else if (cellValue.number !== undefined) {
              // Number object
              cellValue = cellValue.number;
            } else if (cellValue.bool !== undefined) {
              // Boolean object
              cellValue = cellValue.bool;
            } else {
              // Fallback to string representation
              cellValue = String(cellValue);
            }
          }
          
          rowData.push({
            value: cellValue,
            hyperlink: hyperlink,
            imageData: imageData
          });
        });
        
        rows.push(rowData);
      });
      
      if (rows.length > 0) {
        const headerRow = rows[0] || [];
        const headers = headerRow.map(cell => String(cell.value));
        const dataRows = rows.slice(1);
        
        sheets.push({
          sheetName,
          headers,
          rows: dataRows
        });
      }
    });
    
    setExportData(sheets);
  };

  const formatJobDateTime = (dateString: string) => {
    try {
      return formatDateTime(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  const getDeviceNames = (job: ReportJob): string => {
    if (job.result_summary?.selection?.devices_detailed) {
      return job.result_summary.selection.devices_detailed
        .map(device => device.device_name)
        .join(', ');
    }
    
    if (job.result_summary?.selection?.devices) {
      return job.result_summary.selection.devices.join(', ');
    }
    
    return t("job.unknownDevices");
  };

  // Download file function
  const handleDownload = async () => {
    if (!job?.id) return;

    try {
      const downloadResponse = await downloadMutation.mutateAsync({ jobId: job.id });
      if (downloadResponse?.data?.url) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = downloadResponse.data.url;
        link.download = `export_${job.type}_${new Date().toISOString().split('T')[0]}.${job.type === 'boulders_report' ? 'zip' : 'xlsx'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setDataError(t("viewer.downloadFailed"));
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      // Check if it's an Excel file or ZIP file
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type === 'application/zip' ||
          file.name.endsWith('.xlsx') ||
          file.name.endsWith('.zip')) {
        loadExcelData(file);
      } else {
        setDataError(t("viewer.dropInvalid"));
      }
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      loadExcelData(file);
    }
  };

  // Check if value is an image path
  const isImagePath = (value: any): boolean => {
    if (typeof value !== 'string') return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const lowerValue = value.toLowerCase();
    return imageExtensions.some(ext => lowerValue.endsWith(ext)) || 
           lowerValue.startsWith('pub/') || 
           lowerValue.includes('/images/') ||
           lowerValue.includes('/img/');
  };

  // Check if value is a date
  const isDate = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    
    // Check if it's already a Date object
    if (value instanceof Date) return true;
    
    // Check if it's a string that looks like a date
    if (typeof value === 'string') {
      // Common date patterns
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO format
        /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
        /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
        /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
      ];
      
      return datePatterns.some(pattern => pattern.test(value.trim()));
    }
    
    // Check if it's a number that could be a timestamp
    if (typeof value === 'number') {
      // Check if it's a reasonable timestamp (between 1970 and 2100)
      const year = new Date(value).getFullYear();
      return year >= 1970 && year <= 2100;
    }
    
    return false;
  };

  // Cell dates follow the active locale; the field set stays as it was.
  const formatDate = (value: any): string => {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);

      return date.toLocaleString(activeLocale(), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return String(value);
    }
  };

  // Boulders mapper - detect specific columns and apply appropriate formatting
  const getBouldersColumnType = (header: string): 'image' | 'date' | 'default' => {
    const headerLower = header.toLowerCase().trim();
    
    // Check for Image column
    if (headerLower === 'image' || headerLower === 'images' || headerLower === 'photo' || headerLower === 'photos') {
      return 'image';
    }
    
    // Check for date columns (Start, End, Date, etc.)
    if (headerLower === 'start' || headerLower === 'end' || 
        headerLower === 'start_time' || headerLower === 'end_time' ||
        headerLower === 'start_date' || headerLower === 'end_date' ||
        headerLower === 'date' || headerLower === 'time' ||
        headerLower === 'created_at' || headerLower === 'updated_at' ||
        headerLower === 'timestamp') {
      return 'date';
    }
    
    return 'default';
  };

  // Format cell value with boulders mapper support
  const formatCellValue = (cellData: CellData, header?: string, columnIndex?: number): string | React.ReactNode => {
    const value = cellData.value;
    const hyperlink = cellData.hyperlink;
    const imageData = cellData.imageData;
    
    if (value === null || value === undefined) return '';
    
    // Apply boulders mapper if this is a boulders report
    if (job?.type === 'boulders_report' && header && columnIndex !== undefined) {
      const columnType = getBouldersColumnType(header);
      
      switch (columnType) {
        case 'image':
          // For Image column, prioritize extracted image data, then hyperlink, then value
          if (imageData) {
            return <ImageCell imageData={imageData} />;
          } else if (hyperlink && hyperlink.trim()) {
            return <ImageCell src={hyperlink.trim()} />;
          } else if (typeof value === 'string' && value.trim()) {
            return <ImageCell src={value.trim()} />;
          }
          break;
        case 'date':
          // For date columns, always try to format as date
          if (isDate(value)) {
            return formatDate(value);
          }
          break;
        default:
          // For other columns, use default formatting
          break;
      }
    }
    
    // Default formatting logic
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    if (isDate(value)) {
      return formatDate(value);
    }
    if (isImagePath(value)) {
      // Check if we have extracted image data first
      if (imageData) {
        return <ImageCell imageData={imageData} />;
      }
      return <ImageCell src={String(value)} />;
    }
    return String(value);
  };

  // Handle column sorting
  const handleSort = (columnIndex: number) => {
    if (sortColumn === columnIndex) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnIndex);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Reset table state when new data is loaded
  useEffect(() => {
    setSearchTerm("");
    setSortColumn(null);
    setSortDirection('asc');
    setCurrentPage(1);
  }, [exportData]);

  if (jobLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">{t("viewer.loading")}</span>
        </div>
      </div>
    );
  }

  if (jobError || !job) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-8">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            {t("viewer.notFoundTitle")}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t("viewer.notFoundDescription")}
          </p>
          <Button onClick={() => navigate('/export')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("viewer.back")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/export')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("viewer.back")}
          </Button>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">{t("viewer.title")}</h1>
              <p className="text-muted-foreground">
                {t("viewer.subtitle", {
                  type:
                    job.type === 'activity_report'
                      ? t("reportType.activity")
                      : t("reportType.boulders"),
                })}
              </p>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        {job.status === 'SUCCESS' && job.artifact?.exists && (
          <div className="flex gap-2">
            <Button onClick={handleDownload} disabled={downloadMutation.isPending}>
              <Download className="h-4 w-4 mr-2" />
              {downloadMutation.isPending ? t("viewer.downloading") : t("viewer.download")}
            </Button>
          </div>
        )}
      </div>

      {/* Export Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t("viewer.infoTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">{t("viewer.typeLabel")}</span>
              <div className="font-medium">
                {job.type === 'activity_report'
                  ? t("reportType.activity")
                  : t("reportType.boulders")}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">{t("viewer.statusLabel")}</span>
              <div className="font-medium">
                {t(`status.${job.status}`, { defaultValue: job.status })}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">{t("viewer.createdLabel")}</span>
              <div className="font-medium">{formatRelativeTimeUTC(job.created_at)}</div>
            </div>
            {job.result_summary?.window && (
              <div>
                <span className="text-muted-foreground">{t("viewer.timeRangeLabel")}</span>
                <div className="font-medium">
                  {formatJobDateTime(job.result_summary.window.start)} - {formatJobDateTime(job.result_summary.window.end)}
                </div>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">{t("viewer.machinesLabel")}</span>
              <div className="font-medium">{getDeviceNames(job)}</div>
            </div>
            {job.result_summary?.selection?.location && (
              <div>
                <span className="text-muted-foreground">{t("viewer.locationLabel")}</span>
                <div className="font-medium">{job.result_summary.selection.location.location_name}</div>
              </div>
            )}
            {job.result_summary?.outcome && (
              <div>
                <span className="text-muted-foreground">{t("viewer.resultsLabel")}</span>
                <div className="font-medium">
                  {job.result_summary.outcome === 'SUCCESS' ? (
                    <div className="space-y-1">
                      <div>
                        {t("job.totalRows", {
                          count: job.result_summary.counts.total,
                        })}
                      </div>
                      {Object.entries(job.result_summary.counts.datasets).map(([dataset, count]) => (
                        <div key={dataset} className="text-xs text-muted-foreground">
                          {t("job.datasetRows", { dataset, count })}
                        </div>
                      ))}
                    </div>
                  ) : (
                    t("job.noData", {
                      reason:
                        job.result_summary.reason_code ||
                        t("job.reasonEmptyWindow"),
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Drag & Drop Area */}
      <Card 
        className={`border-2 border-dashed transition-colors ${
          isDragOver 
            ? 'border-primary bg-primary/5' 
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardContent className="py-12">
          <div className="text-center">
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {exportData.length > 0
                ? t("viewer.dropReplace")
                : t("viewer.dropTitle")}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t("viewer.dropDescription")}
            </p>
            <div className="flex items-center justify-center gap-4">
              <input
                type="file"
                accept=".xlsx,.zip"
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
              />
              <Button variant="outline" asChild>
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  {t("viewer.chooseFile")}
                </label>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Display */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin mr-3" />
              <div>
                <h3 className="text-lg font-medium">{t("viewer.preparingTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("viewer.preparingDescription")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                {t("viewer.errorTitle")}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {error instanceof Error ? error.message : t("viewer.errorDescription")}
              </p>
              <Button onClick={() => window.location.reload()}>
                {t("viewer.tryAgain")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : exportData.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            {exportData.length === 1 ? (
              // Single sheet - no tabs needed
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">{exportData[0].sheetName}</h3>
                <DataTable
                  headers={exportData[0].headers}
                  data={exportData[0].rows}
                  searchTerm={searchTerm}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  onSort={handleSort}
                  onPageChange={setCurrentPage}
                  onSearchChange={setSearchTerm}
                  onItemsPerPageChange={setItemsPerPage}
                  formatCellValue={formatCellValue}
                />
              </div>
            ) : (
              // Multiple sheets - use tabs
              <Tabs 
                defaultValue={exportData[0].sheetName} 
                className="w-full"
                value={exportData[0].sheetName}
                onValueChange={() => {}}
              >
                <TabsList className="grid w-full grid-cols-3">
                  {exportData.map((sheet) => (
                    <TabsTrigger key={sheet.sheetName} value={sheet.sheetName}>
                      {sheet.sheetName}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {exportData.map((sheet) => (
                  <TabsContent key={sheet.sheetName} value={sheet.sheetName} className="p-6">
                    <DataTable
                      headers={sheet.headers}
                      data={sheet.rows}
                      searchTerm={searchTerm}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      currentPage={currentPage}
                      itemsPerPage={itemsPerPage}
                      onSort={handleSort}
                      onPageChange={setCurrentPage}
                      onSearchChange={setSearchTerm}
                      onItemsPerPageChange={setItemsPerPage}
                      formatCellValue={formatCellValue}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <EmptyState 
              title={t("viewer.emptyTitle")}
              description={t("viewer.emptyDescription")}
              showBorder={false}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExportViewer;
